import classNames from 'classnames';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    CREATE_ORDER_SIDE,
    ACCOUNT_TYPES,
    DEPTH,
    SPOT_CONDITIONAL_ORDER_STATUS_LANG_MAP,
    ORDER_TYPE,
} from 'src/common/constants';
import { currencyFormatter, DateTimeFormatter } from 'src/common/helpers';
import { Button } from 'src/components/core/button';
import { FlexContainer } from 'src/components/core/layout/FlexContainer';
import { StyledTable } from 'src/components/features/common/table/styledTable';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import { cancelConditionalOrder } from 'src/setup/redux/slices/spotSlice';
import { ConditionalOrder, Symbol, OrderTableContentProps } from 'src/types';

const ConditionalOrdersTable: FC<OrderTableContentProps> = ({ isShowAll }) => {
    const { t } = useTranslation();
    const [filterConditionalOrders, setFilterConditionalOrders] = useState(
        [] as Array<ConditionalOrder>,
    );
    const currentSymbol: Symbol = useAppSelector((state) => state.spot.currentSymbol);
    const symbolId = currentSymbol.symbolId;
    const conditionalOrders: Array<ConditionalOrder> = useAppSelector(
        (state) => state.spot.conditionalOpenOrders,
    );
    const symbols = useAppSelector((state) => state.global.configV2.symbol);
    const dispatch = useAppDispatch();

    useEffect(() => {
        let filtered = isShowAll
            ? conditionalOrders
            : conditionalOrders.filter(
                  (conditionalOrder) => conditionalOrder.symbolId === symbolId,
              );
        filtered = [...filtered].sort((a, b) => (a.time - b.time > 0 ? -1 : 1));
        setFilterConditionalOrders(filtered);
    }, [conditionalOrders, symbolId, isShowAll]);

    const cancelOrder = (orderId: string) => {
        dispatch(
            cancelConditionalOrder({
                order_id: orderId,
                client_order_id: new Date().getTime(),
                account_type: ACCOUNT_TYPES.CURRENCY_ACCOUNT,
            }),
        );
    };

    const columns = [
        {
            title: t('spot_pairs'),
            key: 'spotPairs',
            width: '5%',
            render: (order: ConditionalOrder) => {
                return (
                    <span>
                        {order.baseTokenId}
                        <span className="greyText">/{order.quoteTokenId}</span>
                    </span>
                );
            },
        },
        {
            title: t('order_type'),
            key: 'orderType',
            width: '8%',
            render: (order: ConditionalOrder) => {
                return <span>{`${t('conditional')} ${t(order.type.toLowerCase())}`}</span>;
            },
        },
        {
            title: t('direction'),
            key: 'direction',
            width: '4%',
            render: (order: ConditionalOrder) => {
                return (
                    <span
                        className={classNames(
                            {
                                greenText: order.side === CREATE_ORDER_SIDE.BUY,
                            },
                            {
                                redText: order.side === CREATE_ORDER_SIDE.SELL,
                            },
                        )}
                    >
                        {t(order.side.toLowerCase())}
                    </span>
                );
            },
        },
        {
            title: t('order_value'),
            key: 'orderValue',
            width: '6%',
            render: (order: ConditionalOrder) => {
                const orderValue =
                    order.type === ORDER_TYPE.MARKET && order.side === CREATE_ORDER_SIDE.BUY
                        ? order.origQty
                        : order.price * order.origQty;
                return (
                    <span>
                        {currencyFormatter(
                            orderValue,
                            DEPTH[symbols[order.symbolId]?.quotePrecision],
                        )}{' '}
                        <span className="greyText">{order.quoteTokenId}</span>
                    </span>
                );
            },
        },
        {
            title: t('order_qty'),
            key: 'orderQty',
            width: '5%',
            render: (order: ConditionalOrder) => {
                if (order.type === ORDER_TYPE.MARKET && order.side === CREATE_ORDER_SIDE.BUY) {
                    return <span>--</span>;
                }
                return (
                    <span>
                        {currencyFormatter(
                            order.origQty,
                            DEPTH[symbols[order.symbolId]?.basePrecision],
                        )}{' '}
                        <span className="greyText">{order.baseTokenId}</span>
                    </span>
                );
            },
        },
        {
            title: t('order_price'),
            key: 'orderPrice',
            width: '6%',
            render: (order: ConditionalOrder) => {
                if (order.type === ORDER_TYPE.MARKET) {
                    return <span>{t('market')}</span>;
                }
                return (
                    <span>
                        {currencyFormatter(
                            order.price,
                            DEPTH[symbols[order.symbolId]?.minPricePrecision],
                        )}{' '}
                        <span className="greyText">{order.quoteTokenId}</span>
                    </span>
                );
            },
        },

        {
            title: t('trigger_price'),
            key: 'triggerPrice',
            width: '7%',
            render: (order: ConditionalOrder) => {
                const operator = Number(order.quotePrice) > Number(order.triggerPrice) ? '≤' : '≥';
                return (
                    <span>
                        {operator}{' '}
                        {currencyFormatter(
                            order.triggerPrice,
                            DEPTH[symbols[order.symbolId]?.minPricePrecision],
                        )}{' '}
                        <span className="greyText">{order.quoteTokenId}</span>
                    </span>
                );
            },
        },
        {
            title: t('order_status'),
            key: 'status',
            width: '6%',
            render: (order: ConditionalOrder) => {
                return <span>{t(SPOT_CONDITIONAL_ORDER_STATUS_LANG_MAP[order.status])}</span>;
            },
        },
        {
            title: t('order_time'),
            key: 'orderTime',
            width: '8%',
            render: (order: ConditionalOrder) => {
                return <span>{DateTimeFormatter(Number(order.updateTime))}</span>;
            },
        },
        {
            title: t('order_id'),
            key: 'orderId',
            width: '9%',
            render: (order: ConditionalOrder) => {
                return <span>{order.orderId}</span>;
            },
        },
        {
            title: t('action'),
            key: 'action',
            width: '6%',
            render: (order: ConditionalOrder) => {
                return (
                    <FlexContainer styles={{ justifyContent: 'start' }}>
                        <Button
                            variant="outlined"
                            className="action-button"
                            onClick={() => cancelOrder(order.orderId)}
                        >
                            {t('cancel')}
                        </Button>
                    </FlexContainer>
                );
            },
        },
    ];
    return <StyledTable columns={columns} data={filterConditionalOrders} rowKey="orderId" />;
};

export { ConditionalOrdersTable };
